import React, { useEffect, useRef, useState } from 'react';

/**
 * 평가 기준안 웹 문서 편집기 전용: 라벨/열 제목 셀.
 * 평소엔 텍스트만 표시(컴팩트), 마우스 hover·키보드 focus·터치 탭 시 우측 상단에 작은 아이콘(연필 + 추가/삭제) 오버레이.
 * 연필 → 입력창(Enter/blur 저장, Esc 취소, 여러 줄은 Shift+Enter 줄바꿈). 보기·인쇄·엑셀에는 쓰이지 않음.
 */
const WebDocHoverLabel: React.FC<{
  value: string;
  /** 값이 비었을 때 보여줄 기본 텍스트 */
  fallbackText?: string;
  placeholder?: string;
  multiline?: boolean;
  editable?: boolean;
  onCommit?: (value: string) => void;
  /** 연필 옆에 붙는 추가 아이콘(+ / 휴지통) */
  actions?: React.ReactNode;
  ariaLabel?: string;
  className?: string;
}> = ({ value, fallbackText = '', placeholder, multiline = false, editable = true, onCommit, actions, ariaLabel, className }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [touchActive, setTouchActive] = useState(false);
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!editing) return;
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    const end = el.value.length;
    el.setSelectionRange(end, end);
  }, [editing]);

  useEffect(() => {
    if (!touchActive) return;
    const onDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setTouchActive(false);
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [touchActive]);

  const startEdit = () => {
    setDraft(value);
    setEditing(true);
  };
  const commit = () => {
    setEditing(false);
    setTouchActive(false);
    if (draft !== value) onCommit?.(draft);
  };
  const cancel = () => {
    setEditing(false);
    setDraft(value);
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      cancel();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      commit();
    }
  };

  const shown = value.trim() ? value : fallbackText;

  return (
    <div
      ref={rootRef}
      className={`cp-wd-hover${multiline ? ' is-multiline' : ''}${touchActive ? ' is-touch-active' : ''}${editing ? ' is-editing' : ''}${className ? ` ${className}` : ''}`}
      onPointerDown={(e) => {
        if (e.pointerType === 'touch' && !editing) setTouchActive(true);
      }}
    >
      {editing ? (
        multiline ? (
          <textarea
            ref={(el) => {
              inputRef.current = el;
            }}
            className="cp-wd-hover-input"
            rows={Math.max(2, draft.split('\n').length)}
            value={draft}
            placeholder={placeholder}
            aria-label={ariaLabel}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={commit}
          />
        ) : (
          <input
            ref={(el) => {
              inputRef.current = el;
            }}
            type="text"
            className="cp-wd-hover-input"
            value={draft}
            placeholder={placeholder}
            aria-label={ariaLabel}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={commit}
          />
        )
      ) : (
        <span
          className="cp-wd-hover-text"
          onDoubleClick={editable ? startEdit : undefined}
        >
          {shown}
        </span>
      )}
      {!editing && (editable || actions) ? (
        <div className="cp-wd-hover-actions">
          {editable ? (
            <button
              type="button"
              className="cp-wd-hover-btn"
              title="수정"
              aria-label={ariaLabel ? `${ariaLabel} 수정` : '수정'}
              onClick={startEdit}
            >
              <img src="/edit.svg" alt="" width={14} height={14} />
            </button>
          ) : null}
          {actions}
        </div>
      ) : null}
    </div>
  );
};

export default WebDocHoverLabel;
